import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Printer, Flag, Star, Award, Heart, Users, Handshake, Megaphone, ShieldCheck,
  Landmark, Sparkles, Gift, UserPlus, Target, Briefcase, TrendingUp, Trophy,
  Calendar, MapPin, Mail, Phone, Globe, ArrowRight, Coffee, Utensils, Car,
  Wine, Crosshair, CircleDot, Camera, Coins, Droplet, HandHeart, Flag as FlagIcon,
} from "lucide-react";
import { CONTACT, SPONSOR_TIERS, EVENT, SPONSOR_DEADLINES, ACTIVATION_DEFAULTS, ACTIVATION_INCLUDES, IN_KIND_NOTE, BENEFITS_MATRIX, FOUNDING_PARTNER } from "@/data/content";
const badge = "/flat/badge-on-white.jpg";

/* Sponsorship Sales Flyer Series — premium, print-ready (Letter, 816x1056).
   Route: /sales-flyers  ·  /sales-flyers?flyer=N renders a single flyer for PDF export. */
const NAVY = "#0e1e3a", NAVY2 = "#13264a", GOLD = "#c9a24b", RED = "#b42230", INK = "#1c2230", MIST = "#f4f6f9", PAPER = "#fff";
const IMG = {
  flag: "/ff-flag-raise.jpg",
  tribute: "/wayne-walker-tribute.jpg",
  golf: "https://images.unsplash.com/photo-1709525617656-5498eefc7d03?crop=entropy&cs=srgb&fm=jpg&q=80&w=1100",
  golfer: "https://images.unsplash.com/photo-1535131749006-b7f58c99034b?crop=entropy&cs=srgb&fm=jpg&q=80&w=1100",
  trophies: "https://images.unsplash.com/photo-1514820720301-4c4790309f46?crop=entropy&cs=srgb&fm=jpg&q=80&w=1100",
  handshake: "https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&q=80&w=1100",
  ff: "https://images.unsplash.com/photo-1618609252884-c29e29b32205?crop=entropy&cs=srgb&fm=jpg&q=80&w=640",
  police: "https://images.pexels.com/photos/7714759/pexels-photo-7714759.jpeg?auto=compress&cs=tinysrgb&w=640",
  ems: "https://images.pexels.com/photos/6519887/pexels-photo-6519887.jpeg?auto=compress&cs=tinysrgb&w=640",
  responders: "https://images.unsplash.com/photo-1649260257572-91bf6f94cff6?crop=entropy&cs=srgb&fm=jpg&q=80&w=640",
  qr: "/qr-sponsor.png",
};

export default function SalesFlyers() {
  const [scale, setScale] = useState(1);
  const { search } = useLocation();
  const only = new URLSearchParams(search).get("flyer");
  const pages = only ? [FLYERS[Number(only) - 1]].filter(Boolean) : FLYERS;

  useEffect(() => {
    const c = () => { const g = window.innerWidth < 640 ? 16 : 40; setScale(Math.min(1, (window.innerWidth - g * 2) / 816)); };
    c(); window.addEventListener("resize", c); return () => window.removeEventListener("resize", c);
  }, []);

  return (
    <div style={{ background: "#2c3038" }} className="w-full flex flex-col items-center py-8 px-2 sm:px-0 print:p-0 print:bg-white">
      <style>{`
        @media print {
          @page { size: letter; margin: 0; }
          html, body { margin: 0 !important; background: #fff !important; }
          .no-print { display: none !important; }
          .fscale { width: auto !important; height: auto !important; margin: 0 !important; }
          .flyer { box-shadow: none !important; transform: none !important; margin: 0 !important; break-after: page; page-break-after: always; }
          .flyer:last-child { break-after: auto; page-break-after: auto; }
        }
        .flyer, .flyer * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        .flyer { font-family: 'Helvetica Neue', Arial, sans-serif; }
        .flyer h1,.flyer h2,.flyer h3,.flyer .serif { font-family: Georgia, 'Times New Roman', serif; }
      `}</style>
      <button onClick={() => window.print()} data-testid="flyers-print-btn" className="no-print fixed top-4 right-4 z-50 text-xs font-semibold tracking-widest uppercase text-white px-5 py-3 flex items-center gap-2 shadow-lg" style={{ background: RED }}>
        <Printer size={15} /> Print / Save as PDF
      </button>
      {pages.map((F, i) => (
        <div key={i} className="fscale" style={{ width: 816 * scale, height: 1056 * scale, marginBottom: 14 }}>
          <div className="flyer" style={{ width: 816, height: 1056, transform: `scale(${scale})`, transformOrigin: "top left", background: PAPER, position: "relative", overflow: "hidden", boxShadow: "0 12px 44px rgba(0,0,0,.45)" }}>
            <F />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── shared bits ─────────────────────────────────────────── */
function StarBar({ color = RED, light }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <span style={{ height: 2, width: 54, background: color }} /><Star size={13} style={{ color }} fill={color} /><span style={{ height: 2, width: 54, background: color }} />
      {light && null}
    </div>
  );
}
function Kicker({ children, color = RED }) { return <div style={{ color, fontSize: 12, letterSpacing: "0.3em", textTransform: "uppercase", fontWeight: 700 }}>{children}</div>; }
function Badge({ size = 92 }) { return <img src={badge} alt="Heroes & Legacy Golf Classic" style={{ width: size, height: size, objectFit: "contain" }} />; }
function CTA({ label = "Become a Sponsor", dark }) {
  return <div style={{ display: "inline-flex", alignItems: "center", gap: 10, background: RED, color: "#fff", padding: "13px 26px", fontSize: 13, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 700 }}>{label} <ArrowRight size={16} /></div>;
}
function Contact({ light, withQr }) {
  const c = light ? "#e6ebf3" : "#3c4457", ic = GOLD;
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 12.5, color: c }}>
        <span style={{ display: "flex", alignItems: "center", gap: 7 }}><Mail size={13} style={{ color: ic }} /> {CONTACT.sponsorEmail}</span>
        <span style={{ display: "flex", alignItems: "center", gap: 7 }}><Phone size={13} style={{ color: ic }} /> {CONTACT.sponsorPhone}</span>
        <span style={{ display: "flex", alignItems: "center", gap: 7 }}><Globe size={13} style={{ color: ic }} /> {CONTACT.website}</span>
      </div>
      {withQr && (
        <div style={{ textAlign: "center" }}>
          <div style={{ background: "#fff", padding: 6, display: "inline-block", border: `1px solid ${light ? "#566175" : "#e2e6ee"}` }}><img src={IMG.qr} alt="Scan to sponsor" style={{ width: 74, height: 74, display: "block" }} /></div>
          <div style={{ fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", color: light ? "#aeb8ca" : "#8a93a5", marginTop: 5 }}>Scan to Sponsor</div>
        </div>
      )}
    </div>
  );
}
function FootLine() { return <div style={{ fontSize: 9.5, letterSpacing: "0.22em", textTransform: "uppercase", color: "#9aa3b2", textAlign: "center" }}>Wayne Walker Foundation · The Heroes &amp; Legacy Golf Classic · Est. 2027</div>; }
function ImgHead({ src, h = 300, children }) {
  return (
    <div style={{ position: "relative", height: h, overflow: "hidden", background: NAVY }}>
      <img src={src} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
      <div style={{ position: "relative", padding: "42px 56px", height: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end", color: "#fff" }}>{children}</div>
    </div>
  );
}
function Pillar({ icon: Ic, title, desc }) {
  return (
    <div style={{ display: "flex", gap: 13, alignItems: "flex-start" }}>
      <div style={{ width: 40, height: 40, background: NAVY, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Ic size={18} /></div>
      <div><div style={{ fontWeight: 700, color: INK, fontSize: 14 }}>{title}</div><div style={{ color: "#57607a", fontSize: 12, marginTop: 2, lineHeight: 1.5 }}>{desc}</div></div>
    </div>
  );
}
function Card({ icon: Ic, title, desc }) {
  return (
    <div style={{ border: "1px solid #e2e6ee", background: "#fff", padding: 16 }}>
      <div style={{ width: 38, height: 38, background: NAVY, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}><Ic size={17} /></div>
      <div style={{ fontWeight: 700, color: NAVY, fontSize: 13.5, marginTop: 10 }}>{title}</div>
      <div style={{ color: "#57607a", fontSize: 11.5, marginTop: 4, lineHeight: 1.5 }}>{desc}</div>
    </div>
  );
}

/* ── FLYER 1 — Become a Founding Sponsor ─────────────────── */
const F1 = () => (
  <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
    <ImgHead src="/flat/head-flag.jpg" h={356}>
      <div style={{ fontSize: 12, letterSpacing: "0.3em", textTransform: "uppercase", color: GOLD }}>Inaugural Edition · 2027</div>
      <h1 className="serif" style={{ fontSize: 50, lineHeight: 1.03, fontWeight: 700, marginTop: 10 }}>Become a<br /><span style={{ color: GOLD }}>Founding Sponsor</span></h1>
      <p style={{ fontSize: 14, color: "#c7d0e0", marginTop: 12, maxWidth: 560, lineHeight: 1.6 }}>The businesses that step forward first are remembered forever. Help launch a lasting tradition of honoring those who serve.</p>
    </ImgHead>
    <div style={{ padding: "30px 56px 0", flex: 1, display: "flex", flexDirection: "column" }}>
      <Kicker>Why Sponsor the Inaugural Year</Kicker>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px 40px", marginTop: 18 }}>
        <Pillar icon={Landmark} title="A Lasting Legacy" desc="Your name is written into the founding chapter — permanently." />
        <Pillar icon={ShieldCheck} title="Community Leadership" desc="Lead with purpose and show your community you serve." />
        <Pillar icon={Award} title="Premier Recognition" desc="Top-tier visibility across every event channel." />
        <Pillar icon={Heart} title="Long-Term Impact" desc="Direct, meaningful support for our first responders." />
        <Pillar icon={Handshake} title="Business Relationships" desc="Connect with an influential, community-minded audience." />
        <Pillar icon={Star} title="Priority Renewal Invitation" desc={`First invitation to renew before public release. ${FOUNDING_PARTNER.condition}`} />
      </div>
      <div style={{ marginTop: "auto", paddingBottom: 26 }}>
        <div style={{ background: NAVY, padding: "22px 26px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20 }}>
          <div>
            <div className="serif" style={{ color: "#fff", fontSize: 20, fontWeight: 700 }}>Be Part of the Beginning</div>
            <div style={{ color: "#c7d0e0", fontSize: 12.5, marginTop: 4 }}>Founding partnerships are limited. Reserve yours today.</div>
            <div style={{ marginTop: 14 }}><CTA label="Become a Founding Sponsor" /></div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ background: "#fff", padding: 6 }}><img src={IMG.qr} alt="Scan to sponsor" style={{ width: 88, height: 88, display: "block" }} /></div>
            <div style={{ fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", color: "#aeb8ca", marginTop: 5 }}>Scan to Sponsor</div>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: 22, marginTop: 14, fontSize: 11.5, color: "#57607a" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}><Mail size={12} style={{ color: RED }} /> {CONTACT.sponsorEmail}</span>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}><Phone size={12} style={{ color: RED }} /> {CONTACT.sponsorPhone}</span>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}><Globe size={12} style={{ color: RED }} /> {CONTACT.website}</span>
        </div>
      </div>
    </div>
  </div>
);

/* ── FLYER 2 — Support Those Who Serve ───────────────────── */
const SERVE = [
  [Flag, "Firefighters", "/flat/serve-ff.jpg", "Running toward danger so others can run to safety."],
  [ShieldCheck, "Law Enforcement", "/flat/serve-police.jpg", "Protecting and serving our neighborhoods every day."],
  [Heart, "EMS Professionals", "/flat/serve-ems.jpg", "Delivering care in life's most critical moments."],
  [Megaphone, "Emergency Dispatchers", "/flat/serve-responders.jpg", "The steady voices who coordinate every response."],
];
const F2 = () => (
  <div style={{ height: "100%", display: "flex", flexDirection: "column", background: PAPER }}>
    <div style={{ padding: "48px 56px 0", textAlign: "center" }}>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}><StarBar /></div>
      <Kicker>Your Sponsorship Makes It Possible</Kicker>
      <h1 className="serif" style={{ fontSize: 46, lineHeight: 1.04, fontWeight: 700, color: INK, marginTop: 10 }}>Support Those <span style={{ color: RED }}>Who Serve</span></h1>
      <p style={{ fontSize: 13.5, color: "#4a5266", marginTop: 12, maxWidth: 620, margin: "12px auto 0", lineHeight: 1.65 }}>When we call, they come. Every dollar raised through the Classic honors and supports the men and women of public safety who protect our families.</p>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, padding: "28px 56px 0" }}>
      {SERVE.map(([Ic, t, img, d]) => (
        <div key={t} style={{ border: "1px solid #e2e6ee", overflow: "hidden" }}>
          <div style={{ position: "relative", height: 128, background: NAVY }}>
            <img src={img} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
            <div style={{ position: "absolute", left: 14, bottom: 12, display: "flex", alignItems: "center", gap: 8, color: "#fff" }}><Ic size={18} style={{ color: GOLD }} /><span className="serif" style={{ fontSize: 18, fontWeight: 700 }}>{t}</span></div>
          </div>
          <div style={{ padding: "12px 16px", color: "#57607a", fontSize: 12, lineHeight: 1.5 }}>{d}</div>
        </div>
      ))}
    </div>
    <div style={{ marginTop: "auto", padding: "0 56px 30px" }}>
      <div style={{ borderLeft: `4px solid ${GOLD}`, background: MIST, padding: "16px 20px", marginTop: 22 }}>
        <div className="serif" style={{ color: NAVY, fontSize: 18, fontStyle: "italic", lineHeight: 1.4 }}>"They give everything. Together, we can stand with them."</div>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 20 }}>
        <CTA label="Sponsor a Hero" />
        <Contact />
      </div>
      <div style={{ marginTop: 20 }}><FootLine /></div>
    </div>
  </div>
);

/* ── FLYER 3 — Why Sponsor? (6-card) ─────────────────────── */
const WHY = [
  [Target, "Community Impact", "Turn participation into real support for first responders."],
  [Megaphone, "Brand Visibility", "Premium exposure across a full marketing ecosystem."],
  [Briefcase, "Business Development", "Meet decision-makers in a relaxed, memorable setting."],
  [Users, "Employee Engagement", "Give your team a shared, meaningful way to give back."],
  [Heart, "Corporate Citizenship", "Show authentic commitment to your community."],
  [Landmark, "Legacy", "Be a permanent part of a growing tradition of service."],
];
const F3 = () => (
  <div style={{ height: "100%", display: "flex", flexDirection: "column", padding: "48px 56px 30px" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}><div style={{ width: 34, height: 3, background: RED }} /><Flag size={14} style={{ color: NAVY }} /></div>
    <Kicker>The Value of Partnership</Kicker>
    <h1 className="serif" style={{ fontSize: 46, fontWeight: 700, color: INK, marginTop: 8, lineHeight: 1.02 }}>Why <span style={{ color: RED }}>Sponsor?</span></h1>
    <p style={{ fontSize: 13, color: "#4a5266", marginTop: 10, maxWidth: 600, lineHeight: 1.6 }}>Sponsorship delivers value on every level — for your brand, your people, and the community you serve.</p>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginTop: 22 }}>
      {WHY.map(([Ic, t, d]) => <Card key={t} icon={Ic} title={t} desc={d} />)}
    </div>
    <div style={{ marginTop: 20, border: `1px solid ${GOLD}`, background: "#fbf8f1", padding: "14px 18px", display: "flex", gap: 16, alignItems: "center" }}>
      <Award size={26} style={{ color: GOLD, flexShrink: 0 }} />
      <div>
        <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "#8a6d2f", fontWeight: 700 }}>Founding Partner Recognition</div>
        <div style={{ color: "#3c4457", fontSize: 11.5, marginTop: 4, lineHeight: 1.5 }}>Every level committed by {SPONSOR_DEADLINES.foundingDate} is permanently recognized as a Founding Partner — on the website, in every program, and on the Founding Partners wall.</div>
      </div>
    </div>
    <div style={{ marginTop: "auto", display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 22 }}>
      <div><CTA /><div style={{ fontSize: 11, color: "#8a93a5", marginTop: 10 }}>{CONTACT.sponsorEmail} · {CONTACT.sponsorPhone}</div></div>
      <div style={{ textAlign: "center" }}><div style={{ background: "#fff", padding: 6, border: "1px solid #e2e6ee" }}><img src={IMG.qr} alt="" style={{ width: 74, height: 74, display: "block" }} /></div><div style={{ fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", color: "#8a93a5", marginTop: 5 }}>Scan to Sponsor</div></div>
    </div>
  </div>
);

/* ── FLYER 4 — Corporate Sponsorship Opportunities ───────── */
// Rows derive from the canonical BENEFITS_MATRIX (first five columns = core tiers).
const mx = (label) => BENEFITS_MATRIX.find((r) => r.label === label).values.slice(0, 5);
const TIER_BENEFITS = [
  ["Complimentary Foursome(s)", mx("Players").map((v) => (v === false ? false : v.replace(" foursomes", "").replace(" foursome", "")))],
  ["Presenting Naming Rights + Welcome Remarks", [true, false, false, false, false]],
  ["Premier Logo Placement", mx("Logo / signage").map((v) => (v === "Premier" ? true : v === "Prominent" ? "Prominent" : false))],
  ["Hole Sponsorship", mx("Hole sign")],
  ["Event Program Recognition", mx("Event program").map((v) => ({ "Full page": "Full", "1/2 page": "Half", "1/4 page": "Quarter" }[v] || v))],
  ["Website & Social Media", mx("Website").map(() => true)],
  ["Booth / Display Space", mx("Booth / display space")],
  ["Gift Bag Inclusion", mx("Gift-bag promo items")],
];
const F4 = () => (
  <div style={{ height: "100%", display: "flex", flexDirection: "column", padding: "44px 50px 28px" }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <div>
        <Kicker>Corporate Partnership</Kicker>
        <h1 className="serif" style={{ fontSize: 38, fontWeight: 700, color: INK, marginTop: 6, lineHeight: 1.05 }}>Sponsorship<br />Opportunities</h1>
      </div>
      <Badge size={78} />
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 8, marginTop: 18 }}>
      {SPONSOR_TIERS.map((t, i) => (
        <div key={t.key} style={{ position: "relative", border: i === 0 ? `1px solid ${GOLD}` : t.featured ? `2px solid ${RED}` : "1px solid #e2e6ee", background: i === 0 ? "#fbf8f1" : t.featured ? "#fbf4f5" : "#fff", padding: "12px 8px", textAlign: "center" }}>
          {t.featured && <span style={{ position: "absolute", top: -8, left: "50%", transform: "translateX(-50%)", background: RED, color: "#fff", fontSize: 7.5, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", padding: "2px 7px", whiteSpace: "nowrap" }}>Most Popular</span>}
          <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: i === 0 ? "#8a6d2f" : NAVY, fontWeight: 700 }}>{t.name}</div>
          <div className="serif" style={{ fontSize: 20, fontWeight: 700, color: RED, marginTop: 6 }}>${(t.price / 1000).toLocaleString()}K</div>
        </div>
      ))}
    </div>
    <div style={{ marginTop: 16, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: RED, fontWeight: 700 }}>Benefits at a Glance</div>
    <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8, fontSize: 11 }}>
      <thead>
        <tr>
          <th style={{ textAlign: "left", padding: "8px 6px", color: "#8a93a5", fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", borderBottom: `2px solid ${NAVY}` }}>Benefit</th>
          {SPONSOR_TIERS.map((t) => <th key={t.key} style={{ padding: "8px 4px", color: NAVY, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: `2px solid ${NAVY}` }}>{t.name}</th>)}
        </tr>
      </thead>
      <tbody>
        {TIER_BENEFITS.map(([label, vals], ri) => (
          <tr key={label} style={{ background: ri % 2 ? MIST : "#fff" }}>
            <td style={{ padding: "8px 6px", color: INK, fontWeight: 600, borderBottom: "1px solid #eef1f6" }}>{label}</td>
            {vals.map((v, ci) => (
              <td key={`${label}-${ci}`} style={{ padding: "8px 4px", textAlign: "center", color: "#3c4457", borderBottom: "1px solid #eef1f6" }}>
                {v === true ? <span style={{ color: GOLD, fontWeight: 700 }}>✓</span> : v === false ? <span style={{ color: "#c7ccd8" }}>—</span> : v}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
    {/* Sponsor a Hero Team */}
    <div style={{ marginTop: 12 }}>
      <div style={{ border: "1px solid #e2e6ee", background: "#fff", padding: "12px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ background: NAVY, color: "#fff", fontSize: 8.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", padding: "3px 8px" }}>Hero Team</span>
            <span className="serif" style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>Sponsor a Hero Team</span>
            <span style={{ color: RED, fontWeight: 700, fontSize: 14 }}>$2,500</span>
          </div>
          <div style={{ color: "#3c4457", fontSize: 10.5, marginTop: 4, lineHeight: 1.45 }}>Send a four-person first responder team to the course — includes a professional team photo op with your sponsored heroes.</div>
        </div>
        <Flag size={24} style={{ color: NAVY, flexShrink: 0 }} />
      </div>
    </div>
    <div style={{ marginTop: 10, display: "flex", gap: 14, fontSize: 10, color: "#3c4457", lineHeight: 1.45 }}>
      <div style={{ flex: 1, borderLeft: `3px solid ${RED}`, paddingLeft: 8 }}><strong style={{ color: NAVY }}>Founding Partner · by {SPONSOR_DEADLINES.foundingDate}:</strong> permanent Founding Partner recognition on the website and in the program.</div>
      <div style={{ flex: 1, borderLeft: `3px solid ${NAVY}`, paddingLeft: 8 }}><strong style={{ color: NAVY }}>Commit by {SPONSOR_DEADLINES.commitDate}</strong> to guarantee signage, program listing, and gift-bag inclusion.</div>
    </div>
    <div style={{ marginTop: "auto", display: "flex", alignItems: "center", justifyContent: "space-between", background: NAVY, padding: "16px 22px" }}>
      <div>
        <div className="serif" style={{ color: "#fff", fontSize: 17, fontWeight: 700 }}>Reserve Your Sponsorship</div>
        <div style={{ color: "#c7d0e0", fontSize: 11.5, marginTop: 3 }}>{CONTACT.sponsorEmail} · {CONTACT.sponsorPhone} · {CONTACT.website}</div>
      </div>
      <div style={{ textAlign: "center" }}><div style={{ background: "#fff", padding: 5 }}><img src={IMG.qr} alt="" style={{ width: 72, height: 72, display: "block" }} /></div><div style={{ fontSize: 8.5, letterSpacing: "0.12em", textTransform: "uppercase", color: "#aeb8ca", marginTop: 4 }}>Scan to Sponsor</div></div>
    </div>
  </div>
);

/* ── FLYER 5 — The Business Case ─────────────────────────── */
const VALUE = [
  [Megaphone, "Brand Awareness"], [Handshake, "Networking"], [Gift, "Client Appreciation"], [Users, "Employee Engagement"],
  [Heart, "Community Goodwill"], [TrendingUp, "Marketing Exposure"], [UserPlus, "Recruitment"], [Briefcase, "Relationship Building"],
];
const F5 = () => (
  <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
    <ImgHead src="/flat/head-handshake.jpg" h={230}>
      <Kicker color={GOLD}>Measurable Value</Kicker>
      <h1 className="serif" style={{ fontSize: 40, lineHeight: 1.04, fontWeight: 700, marginTop: 8 }}>The Business Case<br />for Sponsorship</h1>
    </ImgHead>
    <div style={{ padding: "26px 56px 0", flex: 1, display: "flex", flexDirection: "column" }}>
      <p style={{ fontSize: 13, color: "#4a5266", lineHeight: 1.6 }}>Sponsorship is more than goodwill — it's a smart investment that delivers returns across your business.</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginTop: 18 }}>
        {VALUE.map(([Ic, t]) => (
          <div key={t} style={{ border: "1px solid #e2e6ee", padding: "16px 8px", textAlign: "center" }}>
            <Ic size={20} style={{ color: NAVY, margin: "0 auto" }} /><div style={{ fontSize: 11, color: INK, marginTop: 8, fontWeight: 600, lineHeight: 1.3 }}>{t}</div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 26, border: `2px solid ${GOLD}`, background: "#fbf8f2", padding: "22px 20px" }}>
        <div style={{ textAlign: "center", fontSize: 10.5, letterSpacing: "0.2em", textTransform: "uppercase", color: "#8a6d2f", fontWeight: 700, marginBottom: 14 }}>The Partnership Equation</div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16 }}>
          {[[Heart, "Community Impact", RED], [null, "+", null], [Briefcase, "Business Value", NAVY], [null, "=", null], [Handshake, "Meaningful Partnership", GOLD]].map(([Ic, label, col]) => (
            Ic === null
              ? <div key={label} className="serif" style={{ fontSize: 30, color: "#8a93a5", fontWeight: 700 }}>{label}</div>
              : <div key={label} style={{ textAlign: "center" }}><div style={{ width: 52, height: 52, borderRadius: "50%", background: col, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto" }}><Ic size={22} /></div><div style={{ fontSize: 11, color: INK, marginTop: 8, fontWeight: 700, maxWidth: 90 }}>{label}</div></div>
          ))}
        </div>
      </div>
      <div style={{ marginTop: "auto", display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: 28, paddingTop: 22 }}>
        <CTA label="Invest in Partnership" />
        <Contact withQr />
      </div>
    </div>
  </div>
);

/* ── FLYER 6 — Founding Partner Recognition ──────────────── */
const F6 = () => (
  <div style={{ height: "100%", background: NAVY, color: "#fff", position: "relative", overflow: "hidden", display: "flex", flexDirection: "column" }}>
    <img src="/flat/cover-navy-dark.jpg" alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
    <div style={{ position: "relative", padding: "56px 56px 34px", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}><StarBar color={GOLD} /></div>
      <Kicker color={GOLD}>A Permanent Place in Our History</Kicker>
      <h1 className="serif" style={{ fontSize: 46, fontWeight: 700, marginTop: 10, lineHeight: 1.05 }}>Founding Partner<br /><span style={{ color: GOLD }}>Recognition</span></h1>
      {/* Premium badge */}
      <div style={{ marginTop: 30, width: 210, height: 210, borderRadius: "50%", border: `3px solid ${GOLD}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", position: "relative", background: "radial-gradient(circle at 50% 35%, #2f3a4e, transparent 70%)" }}>
        <div style={{ position: "absolute", inset: 12, borderRadius: "50%", border: "1px solid #6b6042" }} />
        <Award size={40} style={{ color: GOLD }} />
        <div style={{ fontSize: 12, letterSpacing: "0.24em", textTransform: "uppercase", color: "#fff", marginTop: 10 }}>Founding</div>
        <div className="serif" style={{ fontSize: 26, fontWeight: 700, color: GOLD, lineHeight: 1 }}>Partner</div>
        <div style={{ fontSize: 10, letterSpacing: "0.24em", color: "#c7d0e0", marginTop: 8 }}>EST. 2027</div>
      </div>
      <p style={{ fontSize: 14, color: "#c7d0e0", marginTop: 30, maxWidth: 540, lineHeight: 1.65 }}>Inaugural sponsors become permanent members of the Foundation's story — forever recognized as the organizations that helped establish this tradition of honoring those who serve.</p>
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 10, marginTop: 22 }}>
        {FOUNDING_PARTNER.benefits.map((b) => (
          <div key={b.title} style={{ border: "1px solid #595341", background: "#182742", padding: "9px 16px", fontSize: 11.5, color: "#fff", letterSpacing: "0.04em" }}>{b.title}</div>
        ))}
      </div>
      <div style={{ marginTop: 14, color: GOLD, fontSize: 12, fontWeight: 700, letterSpacing: "0.08em" }}>{FOUNDING_PARTNER.condition}</div>
      <div style={{ marginTop: "auto", paddingTop: 26 }}><CTA label="Claim Founding Status" /></div>
      <div style={{ marginTop: 16, fontSize: 11.5, color: "#c7d0e0" }}>{CONTACT.sponsorEmail} · {CONTACT.sponsorPhone} · {CONTACT.website}</div>
    </div>
  </div>
);

/* ── FLYER 7 — Tournament Overview ───────────────────────── */
const OVERVIEW = [
  [Trophy, "Four-Person Scramble", "18 holes of premium team golf."],
  [Sparkles, "Silent Auction", "Exciting packages that fuel the mission."],
  [Handshake, "Networking", "Connect with community & business leaders."],
  [Award, "Awards Reception", "Lunch, team awards & first responder tributes."],
];
const F7 = () => (
  <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
    <ImgHead src="/flat/head-golf.jpg" h={250}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        <div>
          <Kicker color={GOLD}>{EVENT.edition}</Kicker>
          <h1 className="serif" style={{ fontSize: 40, lineHeight: 1.03, fontWeight: 700, marginTop: 8 }}>Tournament Overview</h1>
          <div style={{ fontSize: 13, color: "#c7d0e0", marginTop: 8 }}>{EVENT.name}</div>
        </div>
        <Badge size={80} />
      </div>
    </ImgHead>
    <div style={{ padding: "24px 56px 0", flex: 1, display: "flex", flexDirection: "column" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        {[[Calendar, "When", EVENT.dateLabel, EVENT.dayLabel], [MapPin, "Where", EVENT.venue, EVENT.venueSub], [Users, "Who", EVENT.teams, EVENT.teamsSub]].map(([Ic, l, m, s]) => (
          <div key={l} style={{ border: "1px solid #e2e6ee", background: MIST, padding: "14px 12px", textAlign: "center" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, color: RED }}><Ic size={13} /><span style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 700 }}>{l}</span></div>
            <div className="serif" style={{ fontSize: 17, fontWeight: 700, color: INK, marginTop: 5 }}>{m}</div>
            <div style={{ fontSize: 11, color: "#57607a", marginTop: 2 }}>{s}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 18 }}>
        {OVERVIEW.map(([Ic, t, d]) => <Pillar key={t} icon={Ic} title={t} desc={d} />)}
      </div>
      <div style={{ borderLeft: `4px solid ${RED}`, background: MIST, padding: "14px 18px", marginTop: 18 }}>
        <div style={{ fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", color: RED, fontWeight: 700 }}>Our Mission</div>
        <div style={{ color: "#3c4457", fontSize: 12.5, marginTop: 5, lineHeight: 1.55 }}>Every dollar raised supports the Wayne Walker Foundation and the first responders who protect our community.</div>
      </div>
      <div style={{ marginTop: "auto", display: "flex", alignItems: "center", justifyContent: "space-between", background: NAVY, padding: "16px 22px", marginBottom: 26 }}>
        <div>
          <div className="serif" style={{ color: "#fff", fontSize: 16, fontWeight: 700 }}>Register a Team · $1,000</div>
          <div style={{ color: "#c7d0e0", fontSize: 11.5, marginTop: 3 }}>Individual $250 · {CONTACT.website}</div>
        </div>
        <div style={{ textAlign: "center" }}><div style={{ background: "#fff", padding: 5 }}><img src={IMG.qr} alt="" style={{ width: 66, height: 66, display: "block" }} /></div><div style={{ fontSize: 8.5, letterSpacing: "0.12em", textTransform: "uppercase", color: "#aeb8ca", marginTop: 4 }}>Register / Sponsor</div></div>
      </div>
    </div>
  </div>
);

/* ── FLYER 8 — Activation Sponsorships ───────── */
const ACT_ICONS = { golf_cart: Car, beverage_cart: Wine, breakfast: Coffee, lunch: Utensils, awards_reception: Trophy, driving_range: Crosshair, putting_green: CircleDot };
const EXCLUSIVE = ACTIVATION_DEFAULTS.map((a) => [ACT_ICONS[a.key] || Star, a.label, a.desc, a.amount]);
const IN_KIND = [
  [Target, "Golf Balls"], [Gift, "Player Gifts"], [Camera, "Photography"], [Coins, "Challenge Coins"], [Droplet, "Hydration Stations"], [HandHeart, "Volunteer Support"],
];
const F8 = () => (
  <div style={{ height: "100%", display: "flex", flexDirection: "column", padding: "42px 50px 26px" }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <div>
        <Kicker>Premium Branding Experiences</Kicker>
        <h1 className="serif" style={{ fontSize: 34, fontWeight: 700, color: INK, marginTop: 6, lineHeight: 1.05 }}>Activation<br />Sponsorships</h1>
      </div>
      <Badge size={70} />
    </div>
    <p style={{ fontSize: 12, color: "#4a5266", marginTop: 10, lineHeight: 1.55, maxWidth: 640 }}>More than a logo — each activation is a signature moment that puts your brand at the heart of the day. One sponsor per activation; secured first-come.</p>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 14px", marginTop: 16 }}>
      {EXCLUSIVE.map(([Ic, t, d, price]) => (
        <div key={t} style={{ display: "flex", gap: 11, alignItems: "flex-start", border: "1px solid #eef1f6", padding: "9px 11px" }}>
          <div style={{ width: 32, height: 32, background: NAVY, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Ic size={15} /></div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
              <div style={{ fontWeight: 700, color: NAVY, fontSize: 12.5 }}>{t}</div>
              <div className="serif" style={{ fontWeight: 700, color: RED, fontSize: 14, whiteSpace: "nowrap" }}>${price.toLocaleString()}</div>
            </div>
            <div style={{ color: "#57607a", fontSize: 10.5, marginTop: 1, lineHeight: 1.4 }}>{d}</div>
          </div>
        </div>
      ))}
    </div>
    <div style={{ marginTop: 14, border: `1px solid ${GOLD}`, background: "#fbf8f1", padding: "10px 14px" }}>
      <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "#8a6d2f", fontWeight: 700 }}>In-Kind Contributions Welcome</div>
      <div style={{ fontSize: 11, color: "#3c4457", marginTop: 4, lineHeight: 1.5 }}><strong style={{ color: NAVY }}>{ACTIVATION_INCLUDES}</strong> {IN_KIND_NOTE}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
        {IN_KIND.map(([Ic, t]) => (
          <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10, color: NAVY, border: "1px solid #e2e6ee", background: "#fff", padding: "3px 8px" }}><Ic size={11} /> {t}</span>
        ))}
      </div>
    </div>
    <div style={{ marginTop: "auto", display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 18 }}>
      <div><CTA label="Claim an Opportunity" /><div style={{ fontSize: 11, color: "#8a93a5", marginTop: 10 }}>{CONTACT.sponsorEmail} · {CONTACT.sponsorPhone} · {CONTACT.website}</div></div>
      <div style={{ textAlign: "center" }}><div style={{ background: "#fff", padding: 6, border: "1px solid #e2e6ee" }}><img src={IMG.qr} alt="" style={{ width: 72, height: 72, display: "block" }} /></div><div style={{ fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", color: "#8a93a5", marginTop: 5 }}>Scan to Sponsor</div></div>
    </div>
  </div>
);

const FLYERS = [F1, F2, F3, F4, F5, F6, F7, F8];
